import {
  Box,
  Button,
  HStack,
  Textarea,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { faCheck, faPencil } from '@fortawesome/free-solid-svg-icons';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SparkelIcon from '../../common/icon/SparkelIcon';
import { ForgeAttributeQueryType, PredicateType } from 'src/gql/graphql';
import { getPredicateLabels } from 'src/domain/dbid-predicates';
import { displayNameFromProperty } from 'src/domain/friendly-properties';
import {
  SPARKEL_GEOMETRIC_PROPERTY_SET,
  SPARKEL_LOCATION_PROPERTY_SET,
} from 'src/domain/geometry/geometric-types';

type AiPromptLinkingPanelProps = {
  isOpen: boolean;
  isUpdatingTable: boolean;
  activePrompt?: string;
  appliedQuery?: ForgeAttributeQueryType | null;
  onApplyPromptLinking?: (input: {
    prompt: string;
    usePromptForLinking: boolean;
  }) => Promise<void> | void;
};

// eslint-disable-next-line complexity
export function AiPromptLinkingPanel({
  isOpen,
  isUpdatingTable,
  activePrompt,
  appliedQuery,
  onApplyPromptLinking,
}: AiPromptLinkingPanelProps): React.ReactElement {
  const { t } = useTranslation(['common', 'table']);

  const [aiPrompt, setAiPrompt] = useState('');
  const [isSubmittingAiPrompt, setIsSubmittingAiPrompt] = useState(false);
  const [awaitingAiResolve, setAwaitingAiResolve] = useState(false);
  const [sawUpdatingAfterAiSubmit, setSawUpdatingAfterAiSubmit] =
    useState(false);
  const [latestAppliedPrompt, setLatestAppliedPrompt] = useState<
    string | undefined
  >(undefined);
  const [isEditing, setIsEditing] = useState(false);

  // Initialize prompt from activePrompt when panel opens
  useEffect(() => {
    if (!isOpen) return;
    setAiPrompt(activePrompt ?? '');
    setLatestAppliedPrompt(activePrompt);
  }, [isOpen, activePrompt]);

  // Keep loading until table resolves after apply; then mark this prompt as applied
  useEffect(() => {
    if (!isOpen) return;
    if (isUpdatingTable) {
      if (awaitingAiResolve) setSawUpdatingAfterAiSubmit(true);
    } else {
      if (awaitingAiResolve && sawUpdatingAfterAiSubmit) {
        setIsSubmittingAiPrompt(false);
        setAwaitingAiResolve(false);
        setSawUpdatingAfterAiSubmit(false);
        setLatestAppliedPrompt(aiPrompt);
        setIsEditing(false);
      }
    }
  }, [
    isOpen,
    isUpdatingTable,
    awaitingAiResolve,
    sawUpdatingAfterAiSubmit,
    aiPrompt,
  ]);

  const isDirty = (latestAppliedPrompt ?? '') !== aiPrompt;

  const appliedPromptTextColor = useColorModeValue('purple.500', 'purple.200');
  const chipBg = useColorModeValue('gray.50', 'gray.700');
  const chipColor = useColorModeValue('purple.700', 'purple.200');
  const isTextareaDisabled = !isEditing && !!latestAppliedPrompt && !isDirty;

  return (
    <Box marginTop={2}>
      {appliedQuery?.predicates &&
        appliedQuery.predicates.length > 0 &&
        activePrompt && (
          <Box marginBottom={2}>
            <Text
              alignSelf={'flex-start'}
              fontSize={'xs'}
              marginLeft={1}
              marginTop={2}
              color="gray.500"
            >
              {t('table:dynamic-link.where')}
            </Text>
            {appliedQuery.predicates.map((p, idx) => {
              const predLabel =
                getPredicateLabels(t).get(p.predicate) ?? p.predicate;
              const showValues =
                p.predicate !== PredicateType.Exists &&
                p.predicate !== PredicateType.NotExists;
              let displayCategory = p.attribute.category;
              let displayName = p.attribute.name;
              if (p.attribute.category === SPARKEL_GEOMETRIC_PROPERTY_SET) {
                displayCategory = t('common:quantities');
                displayName = displayNameFromProperty(p.attribute.name, t);
              } else if (
                p.attribute.category === SPARKEL_LOCATION_PROPERTY_SET
              ) {
                displayCategory = t('common:location');
                displayName = displayNameFromProperty(p.attribute.name, t);
              }
              return (
                <>
                  {idx !== 0 && (
                    <Text
                      alignSelf={'flex-start'}
                      fontSize={'xs'}
                      marginLeft={1}
                      marginTop={2}
                      color="gray.500"
                    >
                      {t('table:dynamic-link.and')}
                    </Text>
                  )}
                  <HStack
                    key={idx}
                    spacing={1}
                    align={'center'}
                    wrap={'wrap'}
                    marginTop={1}
                  >
                    <Box
                      paddingX={2}
                      paddingY={0.5}
                      borderRadius={'md'}
                      backgroundColor={chipBg}
                      color={chipColor}
                      fontSize={'xs'}
                    >
                      {displayCategory} / {displayName}
                    </Box>
                    <Box
                      paddingX={2}
                      paddingY={0.5}
                      borderRadius={'md'}
                      backgroundColor={chipBg}
                      color={chipColor}
                      fontSize={'xs'}
                    >
                      {predLabel}
                    </Box>
                    {showValues && (
                      <Box
                        paddingX={2}
                        paddingY={0.5}
                        borderRadius={'md'}
                        backgroundColor={chipBg}
                        color={chipColor}
                        fontSize={'xs'}
                        maxWidth={'sm'}
                      >
                        {(p.attributeValues ?? [])
                          .filter((v) => v !== '')
                          .join(', ')}
                      </Box>
                    )}
                  </HStack>
                </>
              );
            })}
          </Box>
        )}
      <Text
        alignSelf={'flex-start'}
        fontSize={'xs'}
        marginLeft={1}
        marginTop={2}
        color="gray.500"
      >
        {t('table:prompt-linking.prompt')}
      </Text>
      <Box position={'relative'} role={'group'}>
        <Textarea
          aria-label={'ai prompt input'}
          placeholder={t('table:prompt-linking.placeholder')}
          isDisabled={isSubmittingAiPrompt || isTextareaDisabled}
          minWidth={'300px'}
          fontSize={'xs'}
          borderRadius={'base'}
          color={isTextareaDisabled ? appliedPromptTextColor : undefined}
          _disabled={{
            opacity: 0.9,
            cursor: 'not-allowed',
            _hover: {
              opacity: 0.9,
            },
          }}
          _focus={{ borderColor: 'purple.400' }}
          _dark={{ _focus: { borderColor: 'purple.300' } }}
          variant={'filled'}
          rows={3}
          value={aiPrompt}
          onChange={(e) => setAiPrompt(e.target.value)}
        />
        {isTextareaDisabled && (
          <Button
            size={'xs'}
            colorScheme={'gray'}
            position={'absolute'}
            borderRadius={'md'}
            top={1}
            right={1}
            display={'none'}
            leftIcon={<SparkelIcon icon={faPencil} color="inherit" />}
            _groupHover={{ display: 'inline-flex' }}
            onClick={() => setIsEditing(true)}
          >
            {t('table:prompt-linking.edit')}
          </Button>
        )}
      </Box>
      <HStack justifyContent={'flex-end'} marginTop={2}>
        <Button
          size={'xs'}
          variant={'solid'}
          colorScheme={'purple'}
          borderRadius={'md'}
          leftIcon={<SparkelIcon icon={faCheck} color="inherit" />}
          isLoading={isSubmittingAiPrompt}
          isDisabled={!aiPrompt || !isDirty}
          onClick={async () => {
            if (!onApplyPromptLinking || !aiPrompt) return;
            setIsSubmittingAiPrompt(true);
            setAwaitingAiResolve(true);
            setSawUpdatingAfterAiSubmit(false);
            try {
              await onApplyPromptLinking({
                prompt: aiPrompt,
                usePromptForLinking: true,
              });
            } catch (e) {
              setIsSubmittingAiPrompt(false);
              setAwaitingAiResolve(false);
            }
          }}
        >
          {t('table:prompt-linking.apply')}
        </Button>
      </HStack>
    </Box>
  );
}
